import { inject, Injectable } from '@angular/core';
import { DatetimeIntervalFilterInput, PeriodType } from '@app/domain/gql/types';
import { TranslateService } from '@tolgee/ngx';

@Injectable({
	providedIn: 'root',
})
export class DateIntervalLabelService {
	private readonly translate = inject(TranslateService);
	private readonly this_granularity: Record<PeriodType, string> = {
		DAY: 'today',
		WEEK: 'this_week',
		MONTH: 'this_month',
		YEAR: 'this_year',
	};
	private readonly next_granularity: Record<PeriodType, string> = {
		DAY: 'tomorrow',
		WEEK: 'next_week',
		MONTH: 'next_month',
		YEAR: 'next_year',
	};
	private readonly last_granularity: Record<PeriodType, string> = {
		DAY: 'yesterday',
		WEEK: 'lastweek',
		MONTH: 'last_month',
		YEAR: 'last_year',
	};

	label(interval: DatetimeIntervalFilterInput): string {
		if (interval.offset === 0) {
			return this.translate.instant(this.this_granularity[interval.granularity]);
		}
		if (interval.offset === 1) {
			return this.translate.instant(this.next_granularity[interval.granularity]);
		}
		if (interval.offset === -1) {
			return this.translate.instant(this.last_granularity[interval.granularity]);
		}

		const currentDate = new Date();
		if (interval.granularity === 'DAY') {
			currentDate.setDate(currentDate.getDate() + interval.offset);
			return currentDate.toLocaleDateString('no-NO');
		}

		if (interval.granularity === 'WEEK') {
			return this.calcWeekRange(interval, currentDate);
		}

		if (interval.granularity === 'MONTH') {
			return this.calcMonthRangeLabel(interval, currentDate);
		}

		return (currentDate.getFullYear() + interval.offset).toString();
	}

	default(): string {
		return this.translate.instant('select_granularity');
	}

	private calcMonthRangeLabel(
		interval: DatetimeIntervalFilterInput,
		currentDate: Date
	): string {
		const currentYear = currentDate.getFullYear();
		const currentMonth = currentDate.getMonth();
		let month = currentMonth + 1 + (interval.offset % 12);
		const years = Math.floor(Math.abs(interval.offset / 12));
		if (interval.offset < 0) {
			let year = currentYear - years;
			if (month < 1) {
				month += 11;
				year -= 1;
			}
			return `${month}.${year}`;
		}
		let year = currentYear + years;
		if (month > 12) {
			month -= 12;
			year += 1;
		}
		return `${month}.${year}`;
	}

	private calcWeekRange(
		interval: DatetimeIntervalFilterInput,
		currentDate: Date
	): string {
		const weekDay = currentDate.getDay() - 1;
		currentDate.setDate(currentDate.getDate() - weekDay + interval.offset * 7);
		const nextDate = new Date(currentDate);
		nextDate.setDate(nextDate.getDate() + 6);
		const currentMonth = currentDate.getMonth() + 1;
		const nextMonth = nextDate.getMonth() + 1;
		return `${currentDate.getDate()}.${currentMonth} - ${nextDate.getDate()}.${nextMonth}`;
	}
}
